import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Download,
  Send,
  Check,
  Clock,
  AlertCircle,
  IndianRupee,
  Calendar,
  Filter,
  MoreHorizontal,
  Target,
  Building2,
  TrendingUp,
  Percent,
  Loader2,
  Trash2,
  AlertTriangle,
  List,
  Eye,
  ArrowUpCircle,
  FileText,
  Users,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';

import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Checkbox } from '../../components/ui/checkbox';
import { invoicesAPI, dealsAPI, incentivesAPI, demosAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { formatDateIST } from '../../utils/dateUtils';
import { usePrivacy } from '../../contexts/PrivacyContext';

const getStatusColor = (status) => {
  const colors = {
    DRAFT: 'bg-slate-100 text-slate-700',
    SENT: 'bg-blue-100 text-blue-700',
    PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
    PAID: 'bg-emerald-100 text-emerald-700',
    OVERDUE: 'bg-red-100 text-red-700'
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

// Date Range Helpers
const getMonthsForRange = (rangeType, refDate = new Date()) => {
  const year = refDate.getFullYear();
  const month = refDate.getMonth(); // 0-11
  
  // Helper to format YYYY-MM
  const fmt = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;
  
  // Fiscal Year Helpers (Apr-Mar)
  const currentFyStartYear = month >= 3 ? year : year - 1;
  
  switch (rangeType) {
    case 'CURRENT_MONTH':
      return [fmt(year, month)];
    case 'PAST_MONTH': {
      const d = new Date(year, month - 1, 1);
      return [fmt(d.getFullYear(), d.getMonth())];
    }
    case 'THIS_QUARTER': {
      const qStartMonth = Math.floor(month / 3) * 3;
      return [fmt(year, qStartMonth), fmt(year, qStartMonth + 1), fmt(year, qStartMonth + 2)];
    }
    case 'LAST_QUARTER': {
      const qStartMonth = Math.floor(month / 3) * 3 - 3;
      const d = new Date(year, qStartMonth, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      return [fmt(y, m), fmt(y, m + 1), fmt(y, m + 2)];
    }
    case 'H1': // Apr-Sep of Current FY
      return [
        fmt(currentFyStartYear, 3), fmt(currentFyStartYear, 4), fmt(currentFyStartYear, 5),
        fmt(currentFyStartYear, 6), fmt(currentFyStartYear, 7), fmt(currentFyStartYear, 8)
      ];
    case 'H2': // Oct-Mar of Current FY
      return [
        fmt(currentFyStartYear, 9), fmt(currentFyStartYear, 10), fmt(currentFyStartYear, 11),
        fmt(currentFyStartYear + 1, 0), fmt(currentFyStartYear + 1, 1), fmt(currentFyStartYear + 1, 2)
      ];
    case 'THIS_YEAR': // Calendar Year
      return Array.from({length: 12}, (_, i) => fmt(year, i));
    case 'LAST_YEAR':
      return Array.from({length: 12}, (_, i) => fmt(year - 1, i));
    case 'NEXT_YEAR':
      return Array.from({length: 12}, (_, i) => fmt(year + 1, i));
    case 'THIS_FY': // Apr to Mar
      return [
        ...Array.from({length: 9}, (_, i) => fmt(currentFyStartYear, i + 3)), // Apr-Dec
        ...Array.from({length: 3}, (_, i) => fmt(currentFyStartYear + 1, i))  // Jan-Mar
      ];
    case 'LAST_FY': {
      const lastFyStart = currentFyStartYear - 1;
      return [
        ...Array.from({length: 9}, (_, i) => fmt(lastFyStart, i + 3)),
        ...Array.from({length: 3}, (_, i) => fmt(lastFyStart + 1, i))
      ];
    }
    case 'NEXT_FY': {
      const nextFyStart = currentFyStartYear + 1;
      return [
        ...Array.from({length: 9}, (_, i) => fmt(nextFyStart, i + 3)),
        ...Array.from({length: 3}, (_, i) => fmt(nextFyStart + 1, i))
      ];
    }
    case 'ALL':
      return null; // Special case
    default:
      return [fmt(year, month)];
  }
};

const InvoicesPage = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [invoices, setInvoices] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');
  const [selectedSQDs, setSelectedSQDs] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [newInvoice, setNewInvoice] = useState({
    period_month: '',
    invoice_number: '',
    invoice_date: '',
    notes: ''
  });


  const [paymentDetails, setPaymentDetails] = useState({
    amount_paid: 0,
    payment_date: ''
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // Range Filter State
  const [rangeType, setRangeType] = useState('CURRENT_MONTH');
  const [manualMonth, setManualMonth] = useState(''); // For manual selection if needed, currently using ranges

  // Month filter for Invoices + Outstanding SQD (same style as Client Payouts)
  const [invoiceMonthFilter, setInvoiceMonthFilter] = useState('ALL');
  const [sqdMonthFilter, setSqdMonthFilter] = useState('ALL');

  const [receivablesById, setReceivablesById] = useState({});

  // Outstanding SQD & Client Payouts
  const [outstandingSQD, setOutstandingSQD] = useState([]);
  const [clientPayouts, setClientPayouts] = useState([]);
  
  // Team Incentives (Employee Incentives)
  const [teamIncentives, setTeamIncentives] = useState([]);
  const [teamIncentiveFilter, setTeamIncentiveFilter] = useState('PENDING');
  const [teamMonthFilter, setTeamMonthFilter] = useState('ALL');
  
  // Incentive Detail Modal
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [incentiveDetailOpen, setIncentiveDetailOpen] = useState(false);
  const [incentiveDealDetails, setIncentiveDealDetails] = useState(null);
  const [incentiveDemoDetails, setIncentiveDemoDetails] = useState(null);
  const [loadingIncentiveDetails, setLoadingIncentiveDetails] = useState(false);
  
  // Export Selection
  const [selectedTeamIncentives, setSelectedTeamIncentives] = useState([]);
  const [selectedSQDsForExport, setSelectedSQDsForExport] = useState([]);
  
  // Invoice Details Modal
  const [invoiceDetailsOpen, setInvoiceDetailsOpen] = useState(false);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  
  // Invoice Generation Summary
  const [invoiceGenerationStep, setInvoiceGenerationStep] = useState(1);
  const [invoiceSummary, setInvoiceSummary] = useState([]);
  const [newInvoiceNumber, setNewInvoiceNumber] = useState('');
  const [newInvoiceDate, setNewInvoiceDate] = useState(new Date().toISOString().split('T')[0]);

  // Additional Filters for Company Revenue Section
  const [sqdTypeFilter, setSqdTypeFilter] = useState('ALL'); // ALL, SQD_ONLY, CONVERSION_ONLY
  const [sqdEmployeeFilter, setSqdEmployeeFilter] = useState('ALL');
  const [teamEmployeeFilter, setTeamEmployeeFilter] = useState('ALL');

  // Expanded invoice rows
  const [expandedInvoices, setExpandedInvoices] = useState({});
  
  const toggleInvoiceExpand = (invoiceId) => {
    setExpandedInvoices(prev => ({
      ...prev,
      [invoiceId]: !prev[invoiceId]
    }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invRes, dealsRes, incRes] = await Promise.all([
        invoicesAPI.list({ page_size: 100 }),
        dealsAPI.list({ page_size: 100 }),
        incentivesAPI.listRecords({ limit: 500 })
      ]);
      const invData = invRes.data;
      setInvoices(Array.isArray(invData) ? invData : (invData?.invoices || []));
      const dealsData = dealsRes.data;
      setDeals(Array.isArray(dealsData) ? dealsData : (dealsData?.deals || []));
      
      // Team Incentives
      const incData = incRes.data;
      const records = Array.isArray(incData) ? incData : (incData?.records || []);
      setTeamIncentives(records);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch extra data whenever range changes or deals change
  useEffect(() => {
    const fetchExtraData = async () => {
      try {
        const rcvRes = await incentivesAPI.listReceivables({ page_size: 1000 }); // Increase limit to fetch all
        const receivables = Array.isArray(rcvRes.data) ? rcvRes.data : [];

        const rById = {};
        receivables.forEach(r => { rById[r.id] = r; });
        setReceivablesById(rById);

        // 1. Outstanding Revenue (SQD + Conversion)
        setOutstandingSQD(
          receivables
            .filter(r => r.status === 'EXPECTED')
            .map(r => {
              const desc = r.description || '';
              const isSQD = r.source_type === 'SQD_REVENUE';
              
              // Extract SQD position from description: "SQD #4 for 2025-12..."
              let sqd_position = null;
              if (isSQD) {
                const sqdMatch = desc.match(/SQD\s*#(\d+)/i);
                sqd_position = sqdMatch ? parseInt(sqdMatch[1]) : null;
              }
              
              // Extract tier from tier_info or config_snapshot if available
              // For SQD, tier is the sqd_position (Tier 1 = SQD #1, Tier 2 = SQD #2, etc.)
              const tier = isSQD ? sqd_position : null;
              
              // Tier change would be stored in tier_upgrade_history
              const tier_change = r.tier_upgrade_amount || null;
              
              return {
                id: r.id,
                client: r.client_name || 'Unknown',
                employee: r.employee_name || '-',
                sqd_date: r.created_at ? formatDateIST(r.created_at) : '-',
                period_month: r.period_month,
                source_type: r.source_type,
                sqd_position: sqd_position,
                tier: tier,
                tier_change: tier_change,
                description: desc,
                deal_id: r.deal_id,
                amount: r.amount || 0,
                status: r.status
              };
            })
        );

        // 2. Client Payouts - Filter based on Range
        const targetMonths = getMonthsForRange(rangeType);
        
        const monthReceivables = receivables.filter(r =>
          r.status === 'EXPECTED' &&
          r.source_type !== 'SQD_REVENUE' &&
          (targetMonths === null || targetMonths.includes(r.period_month))
        );

        const dealMap = {};
        (deals || []).forEach(d => { dealMap[d.id] = d; });

        const key = (r) => `${r.deal_id || ''}::${r.client_name || ''}::${r.period_month}`; // Group by Deal+Month
        const grouped = {};

        monthReceivables.forEach(r => {
          const k = key(r);
          const deal = r.deal_id ? dealMap[r.deal_id] : null;

          if (!grouped[k]) {
            const onboarded = deal?.actual_close_date
              ? formatDateIST(deal.actual_close_date)
              : '-';

            grouped[k] = {
              id: k,
              deal_id: r.deal_id,
              client: r.client_name || deal?.company_name || 'Unknown',
              plan: deal?.plan_name || '-',
              onboarded_date: onboarded,
              current_month: r.period_month, // Display the actual month of the payout
              monthly_amount: 0,
              one_time_charge: 0,
              one_time_invoiced: false,
              receivable_ids: []
            };
          }

          grouped[k].receivable_ids.push(r.id);

          if (r.source_type === 'ONE_TIME_CHARGE') {
            grouped[k].one_time_charge += (r.amount || 0);
          } else {
            grouped[k].monthly_amount += (r.amount || 0);
          }
        });

        // Convert to array and sort by month then client
        setClientPayouts(
            Object.values(grouped).sort((a, b) => a.current_month.localeCompare(b.current_month))
        );

      } catch (error) {
        console.error('Failed to fetch extra data:', error);
      }
    };
    fetchExtraData();
  }, [rangeType, deals]);

  const filteredInvoices = invoices.filter(inv => {
    const statusOk = statusFilter === 'all' || inv.status === statusFilter;
    const monthOk = invoiceMonthFilter === 'ALL' || inv.period_month === invoiceMonthFilter;
    return statusOk && monthOk;
  });

  const filteredOutstandingSQD = outstandingSQD.filter(s => {
    const monthOk = sqdMonthFilter === 'ALL' || s.period_month === sqdMonthFilter;
    const employeeOk = sqdEmployeeFilter === 'ALL' || s.employee === sqdEmployeeFilter;
    // Type filter - SQD_REVENUE items are "SQD", others are "Conversion"
    let typeOk = true;
    if (sqdTypeFilter === 'SQD_ONLY') {
      typeOk = s.source_type === 'SQD_REVENUE';
    } else if (sqdTypeFilter === 'CONVERSION_ONLY') {
      typeOk = s.source_type !== 'SQD_REVENUE';
    }
    return monthOk && employeeOk && typeOk;
  });

  const totalPending = invoices
    .filter(i => ['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(i.status))
    .reduce((sum, i) => sum + ((i.amount_invoice || 0) - (i.amount_paid || 0)), 0);

  const totalPaid = invoices
    .filter(i => i.status === 'PAID')
    .reduce((sum, i) => sum + (i.amount_paid || 0), 0);

  const selectedSQDTotal = outstandingSQD
    .filter(s => selectedSQDs.includes(s.id))
    .reduce((sum, s) => sum + (s.amount || 0), 0);

  const selectedPayoutTotal = clientPayouts
    .filter(p => (p.receivable_ids || []).some(id => selectedPayouts.includes(id)))
    .reduce((sum, p) => {
      let amount = (p.monthly_amount || 0);
      if (!p.one_time_invoiced) amount += (p.one_time_charge || 0);
      return sum + amount;
    }, 0);

  const handleGenerateInvoice = async () => {
    // ... same as before
    try {
      const receivableIds = [...selectedSQDs, ...selectedPayouts];
      if (receivableIds.length === 0) {
        toast({
          variant: 'destructive',
          title: 'Select receivables',
          description: 'Please select at least one receivable to invoice.'
        });
        return;
      }

      const res = await invoicesAPI.create({
        period_month: newInvoice.period_month,
        invoice_number: newInvoice.invoice_number || undefined,
        invoice_date: newInvoice.invoice_date ? new Date(newInvoice.invoice_date).toISOString() : undefined,
        receivable_ids: receivableIds,
        notes: newInvoice.notes
      });

      setInvoices([res.data, ...invoices]);
      setIsGenerateOpen(false);
      setSelectedSQDs([]);
      setSelectedPayouts([]);
      setNewInvoice({ period_month: '', invoice_number: '', invoice_date: '', notes: '' });
      fetchData(); 
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create invoice',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
        await invoicesAPI.delete(invoiceToDelete.id);
        setInvoices(prev => prev.filter(i => i.id !== invoiceToDelete.id));
        setDeleteDialogOpen(false);
        setInvoiceToDelete(null);
        window.location.reload(); 
    } catch (error) {
        console.error('Failed to delete invoice:', error);
        toast({
          variant: 'destructive',
          title: 'Failed to delete invoice',
          description: error.response?.data?.detail || error.message
        });
    }
  };

  const markAsSent = (invoiceId) => {
    setInvoices(invoices.map(i => i.id === invoiceId ? { ...i, status: 'SENT' } : i));
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDetails({
      amount_paid: invoice.amount_invoice - invoice.amount_paid,
      payment_date: new Date().toISOString().split('T')[0]
    });
    setIsPaymentOpen(true);
  };

  const recordPayment = () => {
    if (!selectedInvoice) return;
    const newAmountPaid = selectedInvoice.amount_paid + paymentDetails.amount_paid;
    const newStatus = newAmountPaid >= selectedInvoice.amount_invoice ? 'PAID' : 'PARTIALLY_PAID';
    setInvoices(invoices.map(i => i.id === selectedInvoice.id ? {
      ...i,
      amount_paid: newAmountPaid,
      status: newStatus,
      payment_received_date: paymentDetails.payment_date
    } : i));
    setIsPaymentOpen(false);
    setSelectedInvoice(null);
  };

  // Team Incentive Status Handlers
  const handleApproveIncentive = async (id) => {
    try {
      await incentivesAPI.approveRecord(id);
      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to approve',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleMarkPayable = async (id) => {
    try {
      await incentivesAPI.markPayable(id);
      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to mark payable',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await incentivesAPI.markPaid(id);
      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to mark paid',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Open incentive detail modal (for team incentives)
  const handleViewIncentiveDetail = async (inc) => {
    setSelectedIncentive(inc);
    setIncentiveDetailOpen(true);
    setLoadingIncentiveDetails(true);
    setIncentiveDealDetails(null);
    setIncentiveDemoDetails(null);
    
    try {
      if (inc.deal_id) {
        const dealRes = await dealsAPI.get(inc.deal_id);
        setIncentiveDealDetails(dealRes.data);
      }
      if (inc.demo_id) {
        const demoRes = await demosAPI.get(inc.demo_id);
        setIncentiveDemoDetails(demoRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
    } finally {
      setLoadingIncentiveDetails(false);
    }
  };
  
  // Open receivable detail modal (for company revenue/SQD items)
  const handleViewReceivableDetail = async (receivable) => {
    // Set the receivable data with proper structure for the modal
    const detailData = {
      ...receivable,
      metric_type: receivable.source_type,
      amount_earned: receivable.amount,
      user_name: receivable.employee,
      company_name: receivable.client
    };
    setSelectedIncentive(detailData);
    setIncentiveDetailOpen(true);
    setLoadingIncentiveDetails(true);
    setIncentiveDealDetails(null);
    setIncentiveDemoDetails(null);
    
    try {
      if (receivable.deal_id) {
        const dealRes = await dealsAPI.get(receivable.deal_id);
        setIncentiveDealDetails(dealRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch receivable details:', error);
    } finally {
      setLoadingIncentiveDetails(false);
    }
  };

  // Export to CSV
  const exportToCSV = (data, filename, columns) => {
    const headers = columns.map(c => c.label).join(',');
    const rows = data.map(row => 
      columns.map(c => {
        const val = c.accessor(row);
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportTeamIncentives = () => {
    const dataToExport = selectedTeamIncentives.length > 0 
      ? teamIncentives.filter(i => selectedTeamIncentives.includes(i.id))
      : teamIncentives.filter(inc => 
          (teamIncentiveFilter === 'ALL' || inc.status === teamIncentiveFilter) &&
          (teamMonthFilter === 'ALL' || inc.period_month === teamMonthFilter) &&
          (teamEmployeeFilter === 'ALL' || inc.user_name === teamEmployeeFilter)
        );
    
    exportToCSV(dataToExport, 'team_incentives', [
      { label: 'Employee', accessor: r => r.user_name || '' },
      { label: 'Type', accessor: r => r.metric_type || '' },
      { label: 'Company', accessor: r => r.company_name || '' },
      { label: 'Period', accessor: r => r.period_month || '' },
      { label: 'Amount', accessor: r => r.amount_earned || 0 },
      { label: 'Status', accessor: r => r.status || '' },
    ]);
  };

  // Update invoice status and linked incentives
  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.updateStatus(invoiceId, newStatus);
      fetchData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update status',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // View invoice details
  const handleViewInvoiceDetails = (invoice) => {
    setSelectedInvoiceDetails(invoice);
    setInvoiceDetailsOpen(true);
  };

  // Export SQD items
  const exportSQDItems = () => {
    const dataToExport = selectedSQDs.length > 0 
      ? outstandingSQD.filter(s => selectedSQDs.includes(s.id))
      : filteredOutstandingSQD;
    
    exportToCSV(dataToExport, 'company_revenue', [
      { label: 'Client', accessor: r => r.client || '' },
      { label: 'Employee', accessor: r => r.employee || '' },
      { label: 'Type', accessor: r => r.source_type || '' },
      { label: 'Period', accessor: r => r.period_month || '' },
      { label: 'Tier', accessor: r => r.tier || '' },
      { label: 'Tier Change', accessor: r => r.tier_change || '' },
      { label: 'Date', accessor: r => r.sqd_date || '' },
      { label: 'Amount', accessor: r => r.amount || 0 },
    ]);
  };

  // Export Invoices
  const exportInvoices = () => {
    exportToCSV(filteredInvoices, 'invoices', [
      { label: 'Reference', accessor: r => r.reference_number || '' },
      { label: 'Invoice #', accessor: r => r.invoice_number || '' },
      { label: 'Date', accessor: r => r.invoice_date || '' },
      { label: 'Period', accessor: r => r.period_month || '' },
      { label: 'Amount', accessor: r => r.amount_invoice || 0 },
      { label: 'Paid', accessor: r => r.amount_paid || 0 },
      { label: 'Status', accessor: r => r.status || '' },
    ]);
  };

  const toggleSQDSelection = (sqd) => {
    setSelectedSQDs(prev => prev.includes(sqd.id) ? prev.filter(id => id !== sqd.id) : [...prev, sqd.id]);
  };

  const togglePayoutSelection = (payout) => {
    setSelectedPayouts(prev => {
      const allIds = payout.receivable_ids || [];
      const hasAny = allIds.some(id => prev.includes(id));
      return hasAny ? prev.filter(id => !allIds.includes(id)) : [...prev, ...allIds];
    });
  };

  if (loading) {
    return <div className="flex justify-center h-96 items-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Company Invoices</h1>
          <p className="text-slate-500 dark:text-slate-400">Manage invoices and capture incentives</p>
        </div>
        <Button className="bg-slate-800 hover:bg-slate-900 text-white" onClick={() => setIsGenerateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Generate Invoice
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
             <CardContent className="p-4">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg"><IndianRupee className="w-4 h-4 text-blue-600 dark:text-blue-400" /></div>
                 <div><p className="text-xs text-slate-500">Total Invoiced</p><p className="text-lg font-bold">{formatCurrency(invoices.reduce((s, i) => s + i.amount_invoice, 0))}</p></div>
               </div>
             </CardContent>
           </Card>
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
             <CardContent className="p-4">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg"><Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /></div>
                 <div><p className="text-xs text-slate-500">Total Paid</p><p className="text-lg font-bold text-emerald-600">{formatCurrency(invoices.filter(i => i.status === 'PAID').reduce((s, i) => s + (i.amount_paid || 0), 0))}</p></div>
               </div>
             </CardContent>
           </Card>
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
             <CardContent className="p-4">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg"><Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
                 <div><p className="text-xs text-slate-500">Pending Payment</p><p className="text-lg font-bold text-amber-600">{formatCurrency(invoices.filter(i => ['SENT', 'PARTIALLY_PAID'].includes(i.status)).reduce((s, i) => s + ((i.amount_invoice || 0) - (i.amount_paid || 0)), 0))}</p></div>
               </div>
             </CardContent>
           </Card>
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
             <CardContent className="p-4">
               <div className="flex items-center gap-3">
                 <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg"><Target className="w-4 h-4 text-purple-600 dark:text-purple-400" /></div>
                 <div><p className="text-xs text-slate-500">Total Expected (SQD)</p><p className="text-lg font-bold text-purple-600">{formatCurrency(outstandingSQD.reduce((s, i) => s + (i.amount || 0), 0))}</p></div>
               </div>
             </CardContent>
           </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white">Invoices</TabsTrigger>
          <TabsTrigger value="sqd-amounts" className="data-[state=active]:bg-white">Outstanding SQD</TabsTrigger>
          <TabsTrigger value="team-incentives" className="data-[state=active]:bg-white">Team Incentives</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Label>Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="INVOICED">Invoiced</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="PARTIALLY_PAID">Partially Paid</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                </SelectContent>
              </Select>

              <Label className="ml-2">Month:</Label>
              <Select value={invoiceMonthFilter} onValueChange={setInvoiceMonthFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {Array.from(new Set((invoices || []).map(i => i.period_month))).filter(Boolean).sort().map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={exportInvoices}>
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <React.Fragment key={invoice.id}>
                      <TableRow className="hover:bg-slate-50">
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1 h-6 w-6"
                            onClick={() => toggleInvoiceExpand(invoice.id)}
                          >
                            {expandedInvoices[invoice.id] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{invoice.reference_number}</TableCell>
                        <TableCell>{invoice.invoice_number || '-'}</TableCell>
                        <TableCell>{formatDateIST(invoice.invoice_date)}</TableCell>
                        <TableCell><Badge variant="outline">{invoice.period_month}</Badge></TableCell>
                        <TableCell className="font-semibold">{formatCurrency(invoice.amount_invoice)}</TableCell>
                        <TableCell className="text-emerald-600">{formatCurrency(invoice.amount_paid)}</TableCell>
                        <TableCell><Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {invoice.status === 'INVOICED' && (
                              <Button variant="ghost" size="sm" onClick={() => handleUpdateInvoiceStatus(invoice.id, 'SENT')} title="Mark as Sent">
                                <Send className="w-4 h-4 text-blue-500" />
                              </Button>
                            )}
                            {invoice.status === 'SENT' && (
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedInvoiceForPayment(invoice); setIsPaymentOpen(true); }} title="Record Payment">
                                <IndianRupee className="w-4 h-4 text-emerald-500" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => { setInvoiceToDelete(invoice); setDeleteDialogOpen(true); }} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded Row */}
                      {expandedInvoices[invoice.id] && (
                        <TableRow className="bg-slate-50 dark:bg-slate-900">
                          <TableCell colSpan={9} className="p-4">
                            <div className="space-y-4">
                              {/* Summary Cards */}
                              <div className="grid grid-cols-4 gap-4">
                                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                                  <p className="text-xs text-slate-500">Invoice Amount</p>
                                  <p className="text-lg font-bold text-blue-600">{formatCurrency(invoice.amount_invoice)}</p>
                                </div>
                                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                                  <p className="text-xs text-slate-500">Amount Paid</p>
                                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(invoice.amount_paid)}</p>
                                </div>
                                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                                  <p className="text-xs text-slate-500">Balance Due</p>
                                  <p className="text-lg font-bold text-amber-600">{formatCurrency((invoice.amount_invoice || 0) - (invoice.amount_paid || 0))}</p>
                                </div>
                                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
                                  <p className="text-xs text-slate-500">Payment Date</p>
                                  <p className="font-medium">{invoice.payment_received_date ? formatDateIST(invoice.payment_received_date) : '-'}</p>
                                </div>
                              </div>
                              
                              {/* Line Items */}
                              {invoice.items && invoice.items.length > 0 && (
                                <div className="border rounded-lg overflow-hidden bg-white dark:bg-slate-800">
                                  <div className="bg-slate-100 dark:bg-slate-700 px-3 py-2 text-sm font-medium">
                                    Line Items ({invoice.items.length})
                                  </div>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="text-xs">Description</TableHead>
                                        <TableHead className="text-xs">Client</TableHead>
                                        <TableHead className="text-xs">Type</TableHead>
                                        <TableHead className="text-xs">Period</TableHead>
                                        <TableHead className="text-xs text-right">Amount</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {invoice.items.map((item, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="text-sm">{item.description || '-'}</TableCell>
                                          <TableCell className="text-sm">{item.client_name || '-'}</TableCell>
                                          <TableCell><Badge variant="outline" className="text-xs">{item.source_type?.replace('_', ' ')}</Badge></TableCell>
                                          <TableCell className="text-sm">{item.period_month}</TableCell>
                                          <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                              
                              {/* Notes */}
                              {invoice.notes && (
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                                  <p className="text-xs text-amber-600 font-medium">Notes</p>
                                  <p className="text-sm">{invoice.notes}</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {filteredInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-500 py-8">No invoices found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sqd-amounts" className="space-y-3">
          {/* Type Tabs */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
            <Button 
              variant={sqdTypeFilter === 'ALL' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSqdTypeFilter('ALL')}
            >
              All
            </Button>
            <Button 
              variant={sqdTypeFilter === 'SQD_ONLY' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSqdTypeFilter('SQD_ONLY')}
            >
              SQD Only
            </Button>
            <Button 
              variant={sqdTypeFilter === 'CONVERSION_ONLY' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSqdTypeFilter('CONVERSION_ONLY')}
            >
              Conversion Only
            </Button>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Label>Month:</Label>
              <Select value={sqdMonthFilter} onValueChange={setSqdMonthFilter}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {Array.from(new Set((outstandingSQD || []).map(s => s.period_month))).filter(Boolean).sort().map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="ml-2">Employee:</Label>
              <Select value={sqdEmployeeFilter} onValueChange={setSqdEmployeeFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Employees</SelectItem>
                  {Array.from(new Set((outstandingSQD || []).map(s => s.employee))).filter(Boolean).sort().map(emp => (
                    <SelectItem key={emp} value={emp}>{emp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportSQDItems}>
                <Download className="w-4 h-4 mr-2" /> Export {selectedSQDs.length > 0 && `(${selectedSQDs.length})`}
              </Button>
              {selectedSQDs.length > 0 && (
                <Button size="sm" onClick={() => setIsGenerateOpen(true)}>
                  <FileText className="w-4 h-4 mr-2" /> Generate Invoice ({selectedSQDs.length})
                </Button>
              )}
            </div>
          </div>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900">
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectedSQDs.length === filteredOutstandingSQD.length && filteredOutstandingSQD.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedSQDs(filteredOutstandingSQD.map(s => s.id));
                          } else {
                            setSelectedSQDs([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>SQD #</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Tier Change</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOutstandingSQD.map(sqd => (
                    <TableRow 
                      key={sqd.id} 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleViewReceivableDetail(sqd)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedSQDs.includes(sqd.id)} onCheckedChange={() => toggleSQDSelection(sqd)} />
                      </TableCell>
                      <TableCell className="font-medium">{sqd.client}</TableCell>
                      <TableCell>{sqd.employee}</TableCell>
                      <TableCell><Badge variant="outline">{sqd.period_month || '-'}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">
                        {sqd.source_type === 'SQD_REVENUE' ? `#${sqd.sqd_position || '-'}` : '-'}
                      </TableCell>
                      <TableCell>
                        {sqd.source_type === 'SQD_REVENUE' ? (
                          <Badge className="bg-blue-100 text-blue-700">Tier {sqd.tier || '-'}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{sqd.source_type?.replace('_', ' ')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {sqd.tier_change ? (
                          <span className="text-green-600 font-medium flex items-center gap-1">
                            <ArrowUpCircle className="w-3 h-3" />
                            +{formatCurrency(sqd.tier_change)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{sqd.sqd_date}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">{formatCurrency(sqd.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredOutstandingSQD.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-slate-500 py-8">No outstanding SQD items</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Incentives Tab */}
        <TabsContent value="team-incentives" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Label>Status:</Label>
              <Select value={teamIncentiveFilter} onValueChange={setTeamIncentiveFilter}>
                <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PAYABLE">Payable</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Label className="ml-2">Month:</Label>
              <Select value={teamMonthFilter} onValueChange={setTeamMonthFilter}>
                <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {Array.from(new Set((teamIncentives || []).map(i => i.period_month))).filter(Boolean).sort().reverse().map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="ml-2">Employee:</Label>
              <Select value={teamEmployeeFilter} onValueChange={setTeamEmployeeFilter}>
                <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Employees</SelectItem>
                  {Array.from(new Set((teamIncentives || []).map(i => i.user_name))).filter(Boolean).sort().map(emp => (
                    <SelectItem key={emp} value={emp}>{emp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={exportTeamIncentives}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV {selectedTeamIncentives.length > 0 && `(${selectedTeamIncentives.length})`}
            </Button>
          </div>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectedTeamIncentives.length === teamIncentives.filter(inc => 
                          (teamIncentiveFilter === 'ALL' || inc.status === teamIncentiveFilter) &&
                          (teamMonthFilter === 'ALL' || inc.period_month === teamMonthFilter) &&
                          (teamEmployeeFilter === 'ALL' || inc.user_name === teamEmployeeFilter)
                        ).length && selectedTeamIncentives.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTeamIncentives(teamIncentives.filter(inc => 
                              (teamIncentiveFilter === 'ALL' || inc.status === teamIncentiveFilter) &&
                              (teamMonthFilter === 'ALL' || inc.period_month === teamMonthFilter) &&
                              (teamEmployeeFilter === 'ALL' || inc.user_name === teamEmployeeFilter)
                            ).map(i => i.id));
                          } else {
                            setSelectedTeamIncentives([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(teamIncentives || [])
                    .filter(inc => teamIncentiveFilter === 'ALL' || inc.status === teamIncentiveFilter)
                    .filter(inc => teamMonthFilter === 'ALL' || inc.period_month === teamMonthFilter)
                    .filter(inc => teamEmployeeFilter === 'ALL' || inc.user_name === teamEmployeeFilter)
                    .map(inc => (
                    <TableRow 
                      key={inc.id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleViewIncentiveDetail(inc)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedTeamIncentives.includes(inc.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedTeamIncentives([...selectedTeamIncentives, inc.id]);
                            } else {
                              setSelectedTeamIncentives(selectedTeamIncentives.filter(id => id !== inc.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{inc.user_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          inc.metric_type === 'SQD' ? 'bg-orange-100 text-orange-700' :
                          inc.metric_type === 'DEAL_CLOSED' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }>
                          {inc.metric_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{inc.company_name || '-'}</TableCell>
                      <TableCell>{inc.period_month}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(inc.amount_earned)}</TableCell>
                      <TableCell>
                        <Badge className={
                          inc.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                          inc.status === 'PAYABLE' ? 'bg-purple-100 text-purple-700' :
                          inc.status === 'APPROVED' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {inc.status}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {inc.status === 'PENDING' && (
                            <Button variant="ghost" size="sm" onClick={() => handleApproveIncentive(inc.id)} title="Approve">
                              <Check className="w-4 h-4 text-blue-500" />
                            </Button>
                          )}
                          {inc.status === 'APPROVED' && (
                            <Button variant="ghost" size="sm" onClick={() => handleMarkPayable(inc.id)} title="Mark Payable">
                              <IndianRupee className="w-4 h-4 text-purple-500" />
                            </Button>
                          )}
                          {inc.status === 'PAYABLE' && (
                            <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(inc.id)} title="Mark Paid">
                              <TrendingUp className="w-4 h-4 text-emerald-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(teamIncentives || []).filter(inc => 
                    (teamIncentiveFilter === 'ALL' || inc.status === teamIncentiveFilter) &&
                    (teamMonthFilter === 'ALL' || inc.period_month === teamMonthFilter) &&
                    (teamEmployeeFilter === 'ALL' || inc.user_name === teamEmployeeFilter)
                  ).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                        No incentives found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Generate Invoice Modal - Enhanced */}
      <Dialog open={isGenerateOpen} onOpenChange={(open) => { setIsGenerateOpen(open); if (!open) setInvoiceGenerationStep(1); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Invoice</DialogTitle>
            <DialogDescription>
              {invoiceGenerationStep === 1 ? 'Enter invoice details' : 'Review and confirm'}
            </DialogDescription>
          </DialogHeader>
          
          {invoiceGenerationStep === 1 ? (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Invoice Number *</Label>
                  <Input 
                    placeholder="INV-2024-001" 
                    value={newInvoice.invoice_number} 
                    onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})} 
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Invoice Date *</Label>
                  <Input 
                    type="date" 
                    value={newInvoice.invoice_date} 
                    onChange={(e) => setNewInvoice({...newInvoice, invoice_date: e.target.value})} 
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Period Month *</Label>
                <Input 
                  type="month" 
                  value={newInvoice.period_month} 
                  onChange={(e) => setNewInvoice({...newInvoice, period_month: e.target.value})} 
                />
              </div>
              <div className="grid gap-2">
                <Label>Notes (Optional)</Label>
                <Textarea 
                  placeholder="Additional notes for this invoice..."
                  value={newInvoice.notes} 
                  onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})} 
                />
              </div>
              
              {/* Selected Items Summary */}
              {(selectedSQDs.length > 0 || selectedPayouts.length > 0) && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <p className="font-medium text-sm mb-2">Selected Items Summary</p>
                  <div className="space-y-2 text-sm">
                    {selectedSQDs.length > 0 && (
                      <div className="flex justify-between">
                        <span>SQD Items ({selectedSQDs.length})</span>
                        <span className="font-semibold">{formatCurrency(selectedSQDTotal)}</span>
                      </div>
                    )}
                    {selectedPayouts.length > 0 && (
                      <div className="flex justify-between">
                        <span>Client Payouts ({selectedPayouts.length})</span>
                        <span className="font-semibold">{formatCurrency(selectedPayoutTotal)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total Invoice Amount</span>
                      <span className="text-emerald-600">{formatCurrency(selectedSQDTotal + selectedPayoutTotal)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Invoice Preview */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Invoice #</p>
                    <p className="font-semibold">{newInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="font-medium">{newInvoice.invoice_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Period</p>
                    <p className="font-medium">{newInvoice.period_month}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Amount</p>
                    <p className="font-bold text-emerald-600">{formatCurrency(selectedSQDTotal + selectedPayoutTotal)}</p>
                  </div>
                </div>
              </div>
              
              {/* Line Items */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-sm font-medium">Invoice Line Items</div>
                <div className="max-h-[200px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Client</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {outstandingSQD.filter(s => selectedSQDs.includes(s.id)).map(sqd => (
                        <TableRow key={sqd.id}>
                          <TableCell className="text-xs"><Badge variant="outline" className="text-xs">SQD</Badge></TableCell>
                          <TableCell className="text-xs">{sqd.client}</TableCell>
                          <TableCell className="text-xs text-right font-medium">{formatCurrency(sqd.amount)}</TableCell>
                        </TableRow>
                      ))}
                      {clientPayouts.filter(p => (p.receivable_ids || []).some(id => selectedPayouts.includes(id))).map(payout => (
                        <TableRow key={payout.id}>
                          <TableCell className="text-xs"><Badge variant="outline" className="text-xs">Payout</Badge></TableCell>
                          <TableCell className="text-xs">{payout.client}</TableCell>
                          <TableCell className="text-xs text-right font-medium">{formatCurrency(payout.monthly_amount + (payout.one_time_invoiced ? 0 : payout.one_time_charge))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {invoiceGenerationStep === 2 && (
              <Button variant="outline" onClick={() => setInvoiceGenerationStep(1)}>Back</Button>
            )}
            {invoiceGenerationStep === 1 ? (
              <Button 
                onClick={() => setInvoiceGenerationStep(2)}
                disabled={!newInvoice.invoice_number || !newInvoice.invoice_date || !newInvoice.period_month || (selectedSQDs.length === 0 && selectedPayouts.length === 0)}
              >
                Review & Confirm
              </Button>
            ) : (
              <Button onClick={handleGenerateInvoice}>Generate Invoice</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>Items will revert to Expected status.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoice Details Modal */}
      <Dialog open={invoiceDetailsOpen} onOpenChange={setInvoiceDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoiceDetails && (
            <div className="space-y-4">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Invoice #</p>
                  <p className="font-semibold">{selectedInvoiceDetails.invoice_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-medium">{formatDateIST(selectedInvoiceDetails.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Period</p>
                  <p className="font-medium">{selectedInvoiceDetails.period_month}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <Badge className={getStatusColor(selectedInvoiceDetails.status)}>{selectedInvoiceDetails.status}</Badge>
                </div>
              </div>

              {/* Amount Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Invoice Amount</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedInvoiceDetails.amount_invoice)}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xs text-emerald-600">Amount Paid</p>
                  <p className="text-xl font-bold text-emerald-700">{formatCurrency(selectedInvoiceDetails.amount_paid)}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-xs text-amber-600">Balance</p>
                  <p className="text-xl font-bold text-amber-700">{formatCurrency(selectedInvoiceDetails.amount_invoice - selectedInvoiceDetails.amount_paid)}</p>
                </div>
              </div>

              {/* Line Items */}
              {selectedInvoiceDetails.items && selectedInvoiceDetails.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-2 font-medium text-sm">Invoice Items</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoiceDetails.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.client_name || '-'}</TableCell>
                          <TableCell><Badge variant="outline">{item.source_type?.replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Status Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                {selectedInvoiceDetails.status === 'INVOICED' && (
                  <Button onClick={() => { handleUpdateInvoiceStatus(selectedInvoiceDetails.id, 'SENT'); setInvoiceDetailsOpen(false); }}>
                    <Send className="w-4 h-4 mr-2" /> Mark as Sent
                  </Button>
                )}
                {selectedInvoiceDetails.status === 'SENT' && (
                  <Button onClick={() => { setSelectedInvoiceForPayment(selectedInvoiceDetails); setInvoiceDetailsOpen(false); setIsPaymentOpen(true); }}>
                    <IndianRupee className="w-4 h-4 mr-2" /> Record Payment
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Incentive Detail Modal */}
      <Dialog open={incentiveDetailOpen} onOpenChange={setIncentiveDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incentive Calculation Details</DialogTitle>
            <DialogDescription>
              Breakdown of how this incentive was calculated
            </DialogDescription>
          </DialogHeader>
          
          {loadingIncentiveDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : selectedIncentive ? (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Company</p>
                  <p className="font-semibold">{selectedIncentive.company_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Employee</p>
                  <p className="font-medium">{selectedIncentive.user_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Type</p>
                  <Badge variant="outline">{selectedIncentive.metric_type}</Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Period</p>
                  <p className="font-medium">{selectedIncentive.period_month}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-700 font-medium">Total Incentive Amount</span>
                  <span className="text-2xl font-bold text-emerald-600">{formatCurrency(selectedIncentive.amount_earned)}</span>
                </div>
              </div>

              {/* Deal Details */}
              {incentiveDealDetails && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-700 mb-2">Deal Details</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-blue-600">Deal ID</p>
                      <p className="font-mono text-xs">{incentiveDealDetails.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">MRR</p>
                      <p className="font-medium">{formatCurrency(incentiveDealDetails.mrr)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Total Value</p>
                      <p className="font-medium">{formatCurrency(incentiveDealDetails.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Plan</p>
                      <p className="font-medium">{incentiveDealDetails.plan_name || 'Custom'}</p>
                    </div>
                  </div>
                  {incentiveDealDetails.team_incentive_config && (
                    <div className="mt-3 p-2 bg-white rounded">
                      <p className="text-xs text-blue-600 mb-1">Config Snapshot Used</p>
                      <div className="flex gap-4 text-xs">
                        <span>1st Month: {incentiveDealDetails.team_incentive_config['1M'] || 0}%</span>
                        <span>Setup: {incentiveDealDetails.team_incentive_config['OTC'] || 0}%</span>
                        <span>Renewal: {incentiveDealDetails.team_incentive_config['NextYr1M'] || 0}%</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SQD Details */}
              {selectedIncentive.metric_type === 'SQD' && incentiveDemoDetails && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-semibold text-orange-700 mb-2">SQD Details</p>
                  {incentiveDemoDetails.team_sqd_config && (
                    <div className="space-y-2">
                      <p className="text-xs text-orange-600">Config WEF: {incentiveDemoDetails.team_sqd_config.wef_month}</p>
                      <p className="text-xs text-orange-600">SQD Position: #{incentiveDemoDetails.sqd_position_employee}</p>
                      <div className="bg-white p-2 rounded">
                        <p className="text-xs font-medium mb-1">Slab Configuration</p>
                        {incentiveDemoDetails.team_sqd_config.slabs?.map((slab, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span>Tier {idx + 1}: {slab.min}-{slab.max || '∞'} SQDs</span>
                            <span className="font-medium">₹{slab.amount}/SQD</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={
                  selectedIncentive.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                  selectedIncentive.status === 'PAYABLE' ? 'bg-purple-100 text-purple-700' :
                  selectedIncentive.status === 'APPROVED' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }>
                  {selectedIncentive.status}
                </Badge>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              Record payment for invoice {selectedInvoiceForPayment?.invoice_number || selectedInvoiceForPayment?.reference_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoiceForPayment && (
            <div className="space-y-4 py-4">
              {/* Invoice Summary */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Invoice Amount</p>
                    <p className="font-semibold">{formatCurrency(selectedInvoiceForPayment.amount_invoice)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Already Paid</p>
                    <p className="font-semibold text-emerald-600">{formatCurrency(selectedInvoiceForPayment.amount_paid || 0)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Balance Due</p>
                    <p className="text-xl font-bold text-amber-600">
                      {formatCurrency((selectedInvoiceForPayment.amount_invoice || 0) - (selectedInvoiceForPayment.amount_paid || 0))}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Payment Amount *</Label>
                  <Input 
                    type="number" 
                    placeholder="Enter amount"
                    value={paymentDetails.amount_paid}
                    onChange={(e) => setPaymentDetails({...paymentDetails, amount_paid: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Payment Date *</Label>
                  <Input 
                    type="date" 
                    value={paymentDetails.payment_date}
                    onChange={(e) => setPaymentDetails({...paymentDetails, payment_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPaymentDetails({
                    ...paymentDetails, 
                    amount_paid: (selectedInvoiceForPayment.amount_invoice || 0) - (selectedInvoiceForPayment.amount_paid || 0)
                  })}
                >
                  Full Balance
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!selectedInvoiceForPayment) return;
                try {
                  const newTotal = (selectedInvoiceForPayment.amount_paid || 0) + paymentDetails.amount_paid;
                  const newStatus = newTotal >= selectedInvoiceForPayment.amount_invoice ? 'PAID' : 'PARTIALLY_PAID';
                  await invoicesAPI.update(selectedInvoiceForPayment.id, {
                    amount_paid: newTotal,
                    status: newStatus,
                    payment_received_date: paymentDetails.payment_date
                  });
                  // If fully paid, also update linked receivables
                  if (newStatus === 'PAID') {
                    await invoicesAPI.updateStatus(selectedInvoiceForPayment.id, 'PAID');
                  }
                  setIsPaymentOpen(false);
                  setSelectedInvoiceForPayment(null);
                  fetchData();
                } catch (error) {
                  toast({
                    variant: 'destructive',
                    title: 'Failed to record payment',
                    description: error.response?.data?.detail || error.message
                  });
                }
              }}
              disabled={!paymentDetails.amount_paid || paymentDetails.amount_paid <= 0}
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
